// Historie-kolom van de regeltabel: wat rekenden we eerder voor dit product?
//
// De prijscheck (/Api/offertes/price-check) geeft per verstuurde regel een
// resultaat terug, in dezelfde volgorde. Vroeger bewaarden we die lijst en
// lazen hem op POSITIE (priceCheck[i]) — maar zodra de regels verschuiven
// (regel verwijderd/toegevoegd, "sorteer op sectie", een regel die er in de
// check niet in zat) hoorde elk resultaat bij de regel ernaast: "Cameraset
// €250" toonde de historie van de cameraman (~€600), "Monteren" die van de
// kilometers (~€1) (29 sep). Nu koppelen we op het PRODUCT: de sleutel is de
// (genormaliseerde) productnaam + eenheid-categorie (+ Gripp-product-id als
// die er is), en de afwijking rekenen we live uit tegen de huidige stukprijs.

// Zelfde normalisatie als de backend (_norm/_unitCat in routes/offertes.js).
function norm(s) {
  return String(s || '').toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}
function unitCat(u) {
  const n = norm(u);
  if (n.includes('minuut') || n.includes('minute') || n.includes('min film')) return 'minuut film';
  if (n.includes('uur') || n.includes('hour')) return 'uur';
  if (n.includes('dag') || n.includes('day')) return 'dag';
  if (n.includes('km')) return 'km';
  if (n.includes('prijs') || n.includes('price')) return 'prijs';
  return 'stuk';
}

// Productnaam telt volledig mee (ook wat tussen haakjes staat: "(dagprijs)" ≠ "(per uur)").
export function prijsHistorieSleutel(line) {
  const naam = String(line?.description || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (!naam) return null;
  return `${line?.productId ?? ''}|${naam}|${unitCat(line?.unit)}`;
}

// Resultaten van één price-check-aanroep → { sleutel: {median,min,max,count} | {none:true} }.
// `lines` en `results` zijn de verstuurde regels en het antwoord (zelfde volgorde).
export function koppelPrijsHistorie(lines, results) {
  const out = {};
  (lines || []).forEach((l, i) => {
    const key = prijsHistorieSleutel(l);
    if (!key) return;
    const r = (results || [])[i];
    out[key] = r && r.level !== 'none' && r.median
      ? { median: r.median, min: r.min, max: r.max, count: r.count, ...(r.basis ? { basis: r.basis } : {}) }
      : { none: true, ...(r?.basis ? { basis: r.basis } : {}) };
  });
  return out;
}

// Historie per HUIDIGE regel (zelfde lengte/volgorde als `lines`), met de afwijking
// t.o.v. de huidige stukprijs. Onbekend of zonder historie → { level: 'none' } / undefined.
export function prijsHistorieVoorRegels(lines, byKey) {
  return (lines || []).map((l) => {
    const key = prijsHistorieSleutel(l);
    const h = key ? (byKey || {})[key] : undefined;
    if (!h) return undefined;
    const basis = h.basis ? { basis: h.basis } : {};
    if (h.none) return { level: 'none', ...basis };
    const sell = Number(l.sellingprice) || 0;
    const dev = h.median ? Math.round(((sell - h.median) / h.median) * 100) : 0;
    const abs = Math.abs(dev);
    const level = abs <= 15 ? 'ok' : abs <= 40 ? 'check' : 'warn';
    return { level, median: h.median, min: h.min, max: h.max, count: h.count, deviationPct: dev, ...basis };
  });
}

// Regels waarvan de historie nog niet is opgehaald (en ook niet onderweg is).
export function regelsZonderHistorie(lines, byKey, pending = new Set()) {
  const seen = new Set();
  return (lines || []).filter((l) => {
    const key = prijsHistorieSleutel(l);
    if (!key || (byKey || {})[key] || pending.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
